import React from 'react';
import { withTranslation } from 'react-i18next';
import { Box, Alert, AlertTitle, Button, Typography, Stack } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

// Catches render-time exceptions in the subtree so a single failing page
// shows a recoverable error instead of blanking the whole app.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface the real cause for debugging (prod logs + local console).
    console.error('ErrorBoundary caught an error:', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { t, children } = this.props;

    if (!error) return children;

    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error">
          <AlertTitle>{t('common.errorBoundaryTitle')}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('common.errorBoundaryMessage')}
          </Typography>
          <Typography
            variant="caption"
            component="pre"
            sx={{
              display: 'block',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              bgcolor: 'rgba(0,0,0,0.06)',
              borderRadius: 1,
              p: 1,
              mb: 2,
              fontFamily: 'monospace',
            }}
          >
            {error.message || String(error)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="error" onClick={this.handleReset}>
              {t('common.tryAgain')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              {t('common.reload')}
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }
}

export default withTranslation()(ErrorBoundary);
